/**
 * Email Notification Service
 * Handles all email notifications through Resend API
 * Respects user notification preferences
 */

import supabase from './supabase';

class EmailNotificationService {
  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    this.isDevelopment = import.meta.env.MODE === 'development';
    this.preferencesCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.batchQueue = [];
    this.batchTimer = null;
    this.batchDelay = 1000; // 1 second
  }

  /**
   * Send notification email with preference check
   * @param {string} userId - User ID to check preferences
   * @param {string} email - Recipient email
   * @param {string} fullName - Recipient full name
   * @param {string} notificationType - Type of notification
   * @param {object} data - Notification data
   * @returns {Promise<{success: boolean, error?: string, messageId?: string}>}
   */
  async sendWithPreferenceCheck(userId, email, fullName, notificationType, data) {
    try {
      // Check cached preferences first
      let emailSettings = this.getCachedPreferences(userId);
      
      if (!emailSettings) {
        // Get user's notification preferences from database
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('notification_settings')
          .eq('id', userId)
          .single();

        if (profileError) {
          console.error('[EmailNotificationService] Error fetching preferences:', profileError);
          return { success: false, error: 'Could not fetch user preferences' };
        }

        const notificationSettings = userProfile?.notification_settings || {};
        emailSettings = notificationSettings.email || {};
        
        // Cache the preferences
        this.cachePreferences(userId, emailSettings);
      }

      // Check if this notification type is enabled
      // Security alerts and critical notifications should always be sent
      const isSecurityAlert = this.isSecurityNotification(notificationType);
      const isEnabled = emailSettings[notificationType] ?? true; // Default to true if not specified

      if (!isEnabled && !isSecurityAlert) {
        if (this.isDevelopment) {
          console.log(`[EmailNotificationService] Notification type '${notificationType}' disabled by user`);
        }
        return { success: true, message: 'Notification disabled by user preference' };
      }

      // Send the email
      return await this.sendEmail(email, fullName, notificationType, data);
    } catch (error) {
      console.error('[EmailNotificationService] Error in sendWithPreferenceCheck:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email notification through Resend API via Edge Function
   * @param {string} email - Recipient email
   * @param {string} fullName - Recipient full name
   * @param {string} notificationType - Type of notification
   * @param {object} data - Notification data
   * @returns {Promise<{success: boolean, error?: string, messageId?: string}>}
   */
  async sendEmail(email, fullName, notificationType, data) {
    try {
      if (!email) {
        throw new Error('Email address is required');
      }

      // Prepare email payload
      const emailPayload = this.prepareEmailPayload(notificationType, {
        email,
        fullName,
        ...data
      });

      if (!emailPayload) {
        throw new Error(`Unsupported notification type: ${notificationType}`);
      }

      if (this.isDevelopment) {
        console.log(`📧 [EmailNotificationService] Sending ${notificationType} to ${email}`);
        console.log(`📧 Payload:`, emailPayload);
      }

      // Send through Supabase Edge Function
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const response = await fetch(`${this.supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(emailPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('[EmailNotificationService] Edge Function error:', result);
        return {
          success: false,
          error: result?.error || 'Failed to send email'
        };
      }

      if (this.isDevelopment) {
        console.log(`✅ [EmailNotificationService] Email sent successfully`, result);
      }

      return {
        success: true,
        messageId: result?.data?.id || result?.id,
        message: `Email sent to ${email}`
      };

    } catch (error) {
      console.error('[EmailNotificationService] Error sending email:', error);
      return {
        success: false,
        error: error.message || 'Failed to send email notification'
      };
    }
  }

  /**
   * Prepare email payload for specific notification types
   * @param {string} type - Notification type
   * @param {object} data - Email data
   * @returns {object} - Formatted payload for send-email function
   */
  prepareEmailPayload(type, data) {
    const { email, fullName, ...rest } = data;

    switch (type) {
      case 'jobMatches':
      case 'jobAlerts':
        return {
          emailType: 'job-alert',
          email,
          fullName,
          jobTitle: rest.jobTitle || 'New Job Match',
          company: rest.company || '',
          location: rest.location || '',
          salary: rest.salary || '',
          jobDescription: rest.jobDescription || '',
          jobUrl: rest.jobUrl || `${window.location.origin}/job-search`
        };

      case 'applicationUpdates':
      case 'applicationStatus':
      case 'application-status':  // From trigger function
        return {
          emailType: 'application-status',
          email,
          fullName,
          jobTitle: rest.jobTitle || 'Job Application',
          company: rest.company || '',
          status: rest.status || 'updated',
          statusMessage: rest.statusMessage || '',
          nextSteps: rest.nextSteps || ''
        };

      case 'messages':
      case 'newMessage':
        return {
          emailType: 'message-notification',
          email,
          fullName,
          senderName: rest.senderName || 'Someone',
          messagePreview: rest.messagePreview || rest.message || 'You have a new message',
          conversationUrl: rest.conversationUrl || `${window.location.origin}/messages`
        };

      case 'projectInvitations':
      case 'teamInvitation':
        return {
          emailType: 'job-posting-update',
          email,
          fullName,
          jobTitle: rest.projectName || rest.teamName || 'Project Invitation',
          updateType: 'invitation',
          updateMessage: rest.invitationMessage || 'You have been invited to join a project',
          jobUrl: rest.projectUrl || `${window.location.origin}/projects`
        };

      case 'paymentUpdates':
      case 'paymentNotification':
        return {
          emailType: 'job-posting-update',
          email,
          fullName,
          jobTitle: 'Payment Update',
          updateType: 'payment',
          updateMessage: rest.message || 'You have a payment update',
          jobUrl: rest.paymentUrl || `${window.location.origin}/earnings`
        };

      case 'profileViews':
      case 'profileView':
        return {
          emailType: 'profile-view',
          email,
          fullName,
          viewerCompany: rest.viewerCompany || 'An employer',
          viewDate: rest.viewDate || new Date().toLocaleDateString(),
          profileUrl: rest.profileUrl || `${window.location.origin}/profile`
        };

      case 'weeklyDigest':
      case 'digest':
        return {
          emailType: 'job-alert',
          email,
          fullName,
          jobTitle: 'Your Weekly Digest',
          company: 'VA Sprouts',
          location: 'Platform Update',
          jobDescription: rest.digestContent || 'Check out this week\'s opportunities and updates',
          jobUrl: rest.digestUrl || `${window.location.origin}/dashboard`
        };

      case 'marketingEmails':
      case 'marketing':
      case 'newsletter':
        return {
          emailType: 'job-alert',
          email,
          fullName,
          jobTitle: rest.title || 'VA Sprouts Update',
          company: 'VA Sprouts Team',
          jobDescription: rest.content || 'Check out the latest news and opportunities',
          jobUrl: rest.readMoreUrl || `${window.location.origin}/blog`
        };

      case 'securityAlerts':
      case 'security':
      case 'login_success':
      case 'login_failure':
        return {
          emailType: 'login-notification',
          email,
          fullName,
          loginDetails: {
            timestamp: rest.timestamp || new Date().toISOString(),
            location: rest.location || 'Unknown',
            device: rest.device || 'Unknown Device',
            ipAddress: rest.ipAddress || 'Unknown IP'
          }
        };

      case 'systemUpdates':
      case 'system':
        return {
          emailType: 'job-posting-update',
          email,
          fullName,
          jobTitle: 'System Update',
          updateType: 'system',
          updateMessage: rest.message || 'A system update has been made',
          jobUrl: rest.url || `${window.location.origin}/dashboard`
        };

      case 'newApplications':
      case 'newApplication':
        return {
          emailType: 'new-application',
          email,
          fullName,
          jobTitle: rest.jobTitle || 'New Application',
          applicantName: rest.applicantName || 'New Applicant',
          applicationDate: rest.applicationDate || new Date().toLocaleDateString(),
          coverLetterPreview: rest.coverLetterPreview || '',
          applicationUrl: rest.applicationUrl || `${window.location.origin}/applications`
        };

      case 'candidateMatches':
        return {
          emailType: 'candidate-match',
          email,
          fullName,
          jobTitle: rest.jobTitle || 'Candidate Match',
          candidateName: rest.candidateName || 'New Candidate',
          matchScore: rest.matchScore || 0,
          candidateProfileUrl: rest.candidateProfileUrl || `${window.location.origin}/candidates`
        };

      case 'interviews':
      case 'interviewScheduled':
        return {
          emailType: 'interview-scheduled',
          email,
          fullName,
          employerName: rest.employerName || rest.candidateName || 'Hiring Team',
          candidateName: rest.candidateName || rest.employerName || 'Candidate',
          jobTitle: rest.jobTitle || 'Interview',
          interviewDate: rest.interviewDate || new Date().toLocaleDateString(),
          interviewTime: rest.interviewTime || 'TBD',
          interviewType: rest.interviewType || 'Video Call',
          interviewDuration: rest.interviewDuration || 60,
          interviewTimezone: rest.interviewTimezone || 'EST',
          interviewNotes: rest.interviewNotes || ''
        };

      default:
        console.warn(`[EmailNotificationService] Unknown notification type: ${type}`);
        return null;
    }
  }

  /**
   * Send notification to multiple users
   * @param {array} recipients - Array of {userId, email, fullName}
   * @param {string} notificationType - Type of notification
   * @param {object} data - Notification data
   * @returns {Promise<{success: boolean, results: array}>}
   */
  async sendBatch(recipients, notificationType, data) {
    try {
      const results = await Promise.all(
        recipients.map(recipient =>
          this.sendWithPreferenceCheck(
            recipient.userId,
            recipient.email,
            recipient.fullName,
            notificationType,
            data
          )
        )
      );

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (this.isDevelopment) {
        console.log(`[EmailNotificationService] Batch send: ${successful} sent, ${failed} failed`);
      }

      return {
        success: failed === 0,
        results,
        summary: {
          total: results.length,
          successful,
          failed
        }
      };
    } catch (error) {
      console.error('[EmailNotificationService] Error sending batch emails:', error);
      return {
        success: false,
        error: error.message,
        results: []
      };
    }
  }

  /**
   * Log email delivery for tracking and debugging
   * @param {string} email - Recipient email
   * @param {string} type - Email type
   * @param {string} status - Status (sent, failed, pending)
   * @param {object} metadata - Additional metadata
   */
  async logDelivery(email, type, status, metadata = {}) {
    try {
      await supabase
        .from('email_delivery_logs')
        .insert({
          email_address: email,
          email_type: type,
          status,
          metadata,
          sent_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('[EmailNotificationService] Error logging delivery:', error);
    }
  }

  /**
   * Cache user preferences with TTL
   * @param {string} userId - User ID
   * @param {object} preferences - User preferences
   */
  cachePreferences(userId, preferences) {
    this.preferencesCache.set(userId, {
      data: preferences,
      timestamp: Date.now()
    });

    // Auto-cleanup old cache entries
    setTimeout(() => {
      this.preferencesCache.delete(userId);
    }, this.cacheTimeout);
  }

  /**
   * Get cached preferences if still valid
   * @param {string} userId - User ID
   * @returns {object|null} - Cached preferences or null
   */
  getCachedPreferences(userId) {
    const cached = this.preferencesCache.get(userId);
    
    if (!cached) return null;
    
    // Check if cache is still valid
    if (Date.now() - cached.timestamp > this.cacheTimeout) {
      this.preferencesCache.delete(userId);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Clear preferences cache for a user
   * @param {string} userId - User ID
   */
  clearPreferencesCache(userId) {
    if (userId) {
      this.preferencesCache.delete(userId);
    } else {
      this.preferencesCache.clear();
    }
  }

  /**
   * Check if notification type is a security notification
   * @param {string} notificationType - Type of notification
   * @returns {boolean} - True if security notification
   */
  isSecurityNotification(notificationType) {
    const securityTypes = [
      'securityAlerts',
      'security',
      'login_failure',
      'login-notification',
      'password-change',
      'password-reset',
      'email_change_verification'
    ];
    
    return securityTypes.includes(notificationType);
  }

  /**
   * Get cache statistics for monitoring
   * @returns {object} - Cache statistics
   */
  getCacheStats() {
    return {
      size: this.preferencesCache.size,
      maxAge: this.cacheTimeout,
      entries: Array.from(this.preferencesCache.entries()).map(([userId, data]) => ({
        userId,
        age: Date.now() - data.timestamp
      }))
    };
  }

  /**
   * Cleanup old email delivery logs (run periodically)
   * @param {number} daysOld - Delete logs older than this many days
   * @returns {Promise<{success: boolean, deleted: number}>}
   */
  async cleanupOldLogs(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const { data, error } = await supabase
        .from('email_delivery_logs')
        .delete()
        .lt('sent_at', cutoffDate.toISOString())
        .select('id');

      if (error) throw error;

      const deletedCount = data?.length || 0;
      
      if (this.isDevelopment) {
        console.log(`[EmailNotificationService] Cleaned up ${deletedCount} old email logs`);
      }

      return { success: true, deleted: deletedCount };
    } catch (error) {
      console.error('[EmailNotificationService] Error cleaning up logs:', error);
      return { success: false, error: error.message, deleted: 0 };
    }
  }
}

export default new EmailNotificationService();
